import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { getSubverticalOptions, industrySupportsSubverticals } from '../../../models/UiUtils';
import {
	useBlogCardsCategories,
	useHandwrittenCardCategories,
	useSocialCategories,
	useTemplateIndustryCategoriesQuery,
} from '../../../queries';
import { useAccountPreferencesSubverticalSettingsMutation } from '../../../queries/Account/useAccountPreferencesSubverticalSettingsMutation';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { ContentFormalitySelect } from '../../../web/components/subverticals/ContentFormalitySelect';
import { Button } from '../../../web/components-v2/Button';
import { Checkbox } from '../../../web/components-v2/Checkbox';
import { Dialog, DialogPopup } from '../../../web/components-v2/Dialog';
import {
	Dropdown,
	DropdownCheckboxItem,
	DropdownContent,
	DropdownSelectTrigger,
} from '../../../web/components-v2/Dropdown';
import { s } from './styles';

export interface ISubverticalSettingsContentProps {
	account?: Api.IAccount;
	className?: string;
	/** Extra aphrodite styles merged onto the outer container (used by the v2 Dialog to make it fluid). */
	containerStyles?: StyleDeclarationValue[];
	/** Called after a successful save (with the new settings) or on cancel (with no args). */
	onClose: (subverticalSettings?: Api.ISubverticalSettings) => void;
}

export const SubverticalSettingsContent: React.FC<ISubverticalSettingsContentProps> = props => {
	const { account, className, containerStyles, onClose } = props;
	const impersonationContext = { account };
	const [selectedSubverticals, setSelectedSubverticals] = React.useState<string[]>(
		account.preferences.subverticalSettings?.subverticals ?? []
	);
	const [selectedOptOuts, setSelectedOptOuts] = React.useState<string[]>(
		account.preferences.subverticalSettings?.contentLibraryCategoryOptOuts ?? []
	);
	const accountIndustry = account.additionalInfo.industry;
	const emailCategoriesQuery = useTemplateIndustryCategoriesQuery({
		industry: accountIndustry,
	});
	const socialCategoriesQuery = useSocialCategories({
		industry: accountIndustry,
	});
	const hwcCategoriesQuery = useHandwrittenCardCategories({
		industry: accountIndustry,
	});
	const blogCategoriesQuery = useBlogCardsCategories({
		industry: accountIndustry,
	});
	const areAllCategoriesLoaded =
		emailCategoriesQuery.isSuccess &&
		socialCategoriesQuery.isSuccess &&
		hwcCategoriesQuery.isSuccess &&
		blogCategoriesQuery.isSuccess;
	const categories = areAllCategoriesLoaded
		? (Array.from(
				new Set([
					...emailCategoriesQuery.data,
					...socialCategoriesQuery.data,
					...hwcCategoriesQuery.data,
					...blogCategoriesQuery.data,
				])
			) as string[])
		: [];

	const subverticalSettingsMutation = useAccountPreferencesSubverticalSettingsMutation({
		onError: error => {
			logger.logApiError('PUT Account/preferences/subverticalSettings', error);
		},
	});
	const isSaving = subverticalSettingsMutation.isPending;
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('SubverticalSelect');

	const subverticalOptions = getSubverticalOptions(accountIndustry);
	const [contentSubscriber, setContentSubscriber] = React.useState(
		account.preferences.subverticalSettings?.contentSubscriber ?? false
	);
	const [formality, setFormality] = React.useState(
		account.preferences.subverticalSettings?.formality ?? Api.ContentFormality.Formal
	);

	const toggleSubvertical = (option: string, checked: boolean) =>
		setSelectedSubverticals(prev => (checked ? [...prev, option] : prev.filter(sv => sv !== option)));
	const toggleOptOut = (option: string, checked: boolean) =>
		setSelectedOptOuts(prev => (checked ? [...prev, option] : prev.filter(c => c !== option)));

	const handleSave = async () => {
		const subverticalSettings: Api.ISubverticalSettings = {
			contentSubscriber,
			formality,
			subverticals: selectedSubverticals,
			contentLibraryCategoryOptOuts: selectedOptOuts,
		};
		try {
			const result = await subverticalSettingsMutation.mutateAsync({
				subvSettings: subverticalSettings,
				impersonationContext,
			});
			onClose(result?.preferences?.subverticalSettings);
		} catch (error) {
			errorMessages.pushApiError(error);
		}
	};

	const onRequestClose = () => {
		onClose();
	};
	return (
		<div className={`${css(s.container, ...(containerStyles ?? []))} subvertical-select ${className || ''}`}>
			<h1 className={css(s.header)}>Industry & Subvertical Settings</h1>
			<section className={css(s.content)}>
				{industrySupportsSubverticals(accountIndustry) && (
					<>
						<h2 className={css(s.title)}>Subvertical</h2>
						<p className={css(s.subverticalSubtext)}>
							Select subvertical content that you would like to appear as a suggestion.
						</p>
						<div className={css(s.multiselectContainer)}>
							<Dropdown>
								<DropdownSelectTrigger styles={[s.fullTrigger]}>
									{selectedSubverticals.length === 0 ? '-' : selectedSubverticals.join(', ')}
								</DropdownSelectTrigger>
								<DropdownContent styles={[s.dropdownContent]}>
									{subverticalOptions.map(option => (
										<DropdownCheckboxItem
											key={option}
											checked={selectedSubverticals.includes(option)}
											onCheckedChange={checked => toggleSubvertical(option, checked)}
										>
											{option}
										</DropdownCheckboxItem>
									))}
								</DropdownContent>
							</Dropdown>
						</div>
					</>
				)}

				<h2 className={css(s.optOut)}>Opt Out</h2>
				<p className={css(s.optOutSubtext)}>Select any content that you would not like to appear as a suggestion.</p>
				<div className={css(s.multiselectContainer)}>
					{areAllCategoriesLoaded ? (
						<Dropdown>
							<DropdownSelectTrigger styles={[s.fullTrigger]}>
								{selectedOptOuts.length === 0
									? '-'
									: `Excluding ${selectedOptOuts.length} ${selectedOptOuts.length === 1 ? 'category' : 'categories'}`}
							</DropdownSelectTrigger>
							<DropdownContent styles={[s.dropdownContent]}>
								{categories.map(option => (
									<DropdownCheckboxItem
										key={option}
										checked={selectedOptOuts.includes(option)}
										onCheckedChange={checked => toggleOptOut(option, checked)}
									>
										{option}
									</DropdownCheckboxItem>
								))}
							</DropdownContent>
						</Dropdown>
					) : (
						<div className={css(s.spinnerContainer)}>
							<LoadingSpinner />
						</div>
					)}
				</div>
				<label className={css(s.checkboxRow)}>
					<Checkbox checked={contentSubscriber} onCheckedChange={checked => setContentSubscriber(checked === true)} />
					<span className={css(s.checkboxLabel)}>Subscribe to new content</span>
				</label>
				<ContentFormalitySelect formality={formality} setFormality={setFormality} styles={[s.formality]} />
			</section>
			<footer className={css(s.rowContainer)}>
				<Button onClick={handleSave} disabled={isSaving} isLoading={isSaving}>
					Save
				</Button>
				<Button variant='secondary' onClick={onRequestClose} disabled={isSaving}>
					Cancel
				</Button>
			</footer>
		</div>
	);
};

export interface ISubverticalSettingsDialogProps {
	account?: Api.IAccount;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

/** Used by the Account Overview Customizations card. */
export const SubverticalSettingsDialog: React.FC<ISubverticalSettingsDialogProps> = ({
	account,
	open,
	onOpenChange,
}) => (
	<Dialog open={open} onOpenChange={onOpenChange} dismissOnOutsideClick={false}>
		<DialogPopup styles={[s.dialogPopup]}>
			<SubverticalSettingsContent
				account={account}
				containerStyles={[s.dialogContainer]}
				onClose={() => onOpenChange(false)}
			/>
		</DialogPopup>
	</Dialog>
);
